/**
 * Shared plumbing for copilot actions the user must confirm before they run.
 *
 * A confirmed action tool never mutates in its handler: it validates the request
 * and returns a `pending` result carrying the copy for the confirmation card
 * ({@link CopilotActionConfirmation}). The card renders Confirm/Cancel, runs the
 * registered executor on confirm, and resumes the agent with the outcome.
 *
 * Register one with `useCopilotConfirmedActionTool` and give the tool a
 * `visible` / `confirm_action` presentation in the tool registry.
 */
import { type ToolResult } from 'shared/copilot/toolResult'

export type CopilotConfirmedActionDecision = 'approved' | 'rejected'

export type CopilotConfirmedActionStatus = CopilotConfirmedActionDecision | 'pending' | 'failed'

/** One thing the action affects, rendered as a chip on the confirmation card. */
export interface CopilotActionConfirmationItem {
  label: string
  /** Secondary text shown as a chip tooltip, e.g. an account or rule id. */
  annotation?: string
  /** Left Chip adornment image URL (`avatarSrc`). */
  iconUrl?: string
  /**
   * When `kind` is `single_select` or `multi_select` (or deprecated `chooseOne`),
   * this id is passed to execute as `selectedValue` / an entry in `selectedValues`.
   * If omitted, the same label-index key used for chip selection is passed instead.
   */
  value?: string
}

/**
 * How the confirmation card behaves:
 * - `confirmation` — show items; Confirm/Cancel with no chip selection
 * - `single_select` — user must pick exactly one item, then Confirm
 * - `multi_select` — user must pick one or more items, then Confirm (chips toggle)
 */
export type CopilotActionConfirmationKind = 'confirmation' | 'single_select' | 'multi_select'

/** Serializable copy for the confirmation card. Owned by the calling page. */
export interface CopilotActionConfirmation {
  /** Card heading, phrased as a question — e.g. `Delete data source?`. */
  title: string
  /** Sentence above the items — e.g. `Alvie AI wants to delete this data source:`. */
  description: string
  items: CopilotActionConfirmationItem[]
  /** Consequence the user should read before confirming. */
  warning?: string
  /** Confirm button label — e.g. `Delete`. */
  confirmLabel: string
  /** Cancel button label. Defaults to the shared `Cancel` copy. */
  cancelLabel?: string
  /** Renders the confirm button as destructive. */
  isDestructive?: boolean
  /**
   * Card behaviour. Defaults to `confirmation` when omitted (and `chooseOne` is
   * not set). Selecting a chip never runs the action by itself for select kinds.
   */
  kind?: CopilotActionConfirmationKind
  /**
   * @deprecated Use `kind: 'single_select'` instead. When `kind` is omitted,
   * `chooseOne: true` means `single_select`.
   */
  chooseOne?: boolean
  /** Label on the applied card once confirmed — e.g. `Deleted`. */
  appliedLabel: string
  /** Chip label when the user cancels — e.g. `Deletion cancelled`. */
  cancelledLabel: string
}

/**
 * When prepare returns this, the confirmed-action handler runs execute immediately
 * (no Confirm/Cancel card). Use for unique-match open-only flows where confirmation is only
 * needed to pick among several targets.
 */
export interface CopilotConfirmedActionExecuteImmediately {
  executeImmediately: true
}

/** Outcome of a confirmed-action `prepare` call. */
export type CopilotConfirmedActionPrepareOutcome =
  | CopilotActionConfirmation
  | ToolResult
  | CopilotConfirmedActionExecuteImmediately

/** True when prepare asked to skip the card and run execute now. */
export const isCopilotConfirmedActionExecuteImmediately = (
  prepared: CopilotConfirmedActionPrepareOutcome
): prepared is CopilotConfirmedActionExecuteImmediately =>
  'executeImmediately' in prepared && prepared.executeImmediately

/** Chip label text. Annotation is shown as a tooltip, not inlined. */
export const confirmationItemDisplayLabel = (item: CopilotActionConfirmationItem): string => item.label

/** Tooltip title for a confirmation chip when annotation is distinct from the label. */
export const confirmationItemTooltipTitle = (item: CopilotActionConfirmationItem): string | undefined =>
  item.annotation != null && item.annotation.length > 0 && item.annotation !== item.label ? item.annotation : undefined

/** Stable key for a confirmation item — used for single_select / multi_select state. */
export const getCopilotActionConfirmationItemKey = (item: CopilotActionConfirmationItem, index: number): string =>
  item.value ?? `${item.label}-${index}`

/** Resolves effective card kind: explicit `kind`, else `chooseOne` → `single_select`, else `confirmation`. */
export const resolveCopilotActionConfirmationKind = (
  confirmation: CopilotActionConfirmation
): CopilotActionConfirmationKind => {
  if (confirmation.kind != null) return confirmation.kind
  if (confirmation.chooseOne === true) return 'single_select'
  return 'confirmation'
}

/** Internal chip-selection mode derived from the resolved confirmation kind. */
export type CopilotActionConfirmationChoiceMode = 'one' | 'any' | null

export const getCopilotActionConfirmationChoiceMode = (
  confirmation: CopilotActionConfirmation
): CopilotActionConfirmationChoiceMode => {
  const kind = resolveCopilotActionConfirmationKind(confirmation)
  if (kind === 'single_select') return 'one'
  if (kind === 'multi_select') return 'any'
  return null
}

/** Builds execute args and approval copy after the user picks chip(s). */
export const buildCopilotConfirmedActionExecuteContext = ({
  args,
  confirmation,
  selectedItemKeys
}: {
  args: Record<string, unknown>
  confirmation: CopilotActionConfirmation
  selectedItemKeys: string[]
}): {
  selectedItems: CopilotActionConfirmationItem[]
  executeArgs: Record<string, unknown>
  approvalConfirmation: CopilotActionConfirmation
} => {
  const selectedEntries = confirmation.items
    .map((item, index) => {
      const key = getCopilotActionConfirmationItemKey(item, index)
      return { item, key }
    })
    .filter(({ key }) => selectedItemKeys.includes(key))

  const selectedItems = selectedEntries.map(({ item }) => item)
  // Prefer item.value; fall back to the selection key so select kinds
  // still apply when chips were keyed by label-index because value was missing.
  const selectedValues = selectedEntries.map(({ item, key }) => item.value ?? key)
  const choiceMode = getCopilotActionConfirmationChoiceMode(confirmation)

  let executeArgs: Record<string, unknown> = args
  if (choiceMode === 'one' && selectedValues[0] != null) {
    executeArgs = { ...args, selectedValue: selectedValues[0] }
  } else if (choiceMode === 'any' && selectedValues.length > 0) {
    executeArgs = { ...args, selectedValues }
  }

  const approvalConfirmation: CopilotActionConfirmation =
    selectedItems.length > 0
      ? { ...confirmation, items: selectedItems, kind: 'confirmation', chooseOne: undefined }
      : confirmation

  return { selectedItems, executeArgs, approvalConfirmation }
}

/**
 * Terminal or pending confirmed-action result sent back to the agent.
 * Page `prepare` / `execute` handlers may attach extra payload fields
 * (e.g. `recommendation`, `table`, `isDataLoaded`); wrappers must keep them.
 */
export interface CopilotConfirmedActionToolResult {
  success: boolean
  status: CopilotConfirmedActionStatus
  message: string
  confirmation?: CopilotActionConfirmation
}

/** Runs the action after the user confirms. Registered per tool name. */
export type CopilotConfirmedActionExecutor = (args: Record<string, unknown>) => Promise<ToolResult> | ToolResult

const copilotConfirmedActionExecutorsByToolName = new Map<string, CopilotConfirmedActionExecutor>()

/**
 * Registers the executor a confirmation card calls when the user confirms.
 * Returns an unregister callback for the owning hook's cleanup.
 */
export const registerCopilotConfirmedActionExecutor = (
  toolName: string,
  executor: CopilotConfirmedActionExecutor
): (() => void) => {
  copilotConfirmedActionExecutorsByToolName.set(toolName, executor)

  return () => {
    if (copilotConfirmedActionExecutorsByToolName.get(toolName) === executor) {
      copilotConfirmedActionExecutorsByToolName.delete(toolName)
    }
  }
}

/**
 * Looks up the executor for a tool name.
 * Returns undefined when the owning page has unmounted, which the card reports
 * back to the agent instead of silently doing nothing.
 */
export const getCopilotConfirmedActionExecutor = (toolName: string): CopilotConfirmedActionExecutor | undefined =>
  copilotConfirmedActionExecutorsByToolName.get(toolName)

/**
 * Builds the pending result a confirmed action handler returns.
 * Nothing has happened yet at this point — the card takes over from here.
 */
export const buildCopilotActionConfirmationRequest = (
  confirmation: CopilotActionConfirmation
): CopilotConfirmedActionToolResult => ({
  success: true,
  status: 'pending',
  message: `Awaiting confirmation: ${confirmation.title}`,
  confirmation
})

/**
 * Wraps a plain {@link ToolResult} from `prepare` (no-op or validation refusal)
 * as a terminal confirmed-action result so the card can render it and
 * `message_end` placement treats it as settled during the follow-up run.
 */
export const buildCopilotConfirmedActionPrepareResult = (result: ToolResult): CopilotConfirmedActionToolResult => ({
  ...result,
  success: result.success,
  status: result.success ? 'approved' : 'failed',
  message: result.message
})

/**
 * Reads the confirmation payload from a pending tool result.
 * Accepts the JSON string AG-UI delivers as well as an already-parsed object.
 */
export const parseCopilotConfirmedActionResult = (
  content: unknown
): {
  status: CopilotConfirmedActionStatus | null
  message: string | null
  confirmation: CopilotActionConfirmation | null
  success: boolean | undefined
} => {
  let parsed: unknown = content

  if (typeof content === 'string') {
    try {
      parsed = JSON.parse(content)
    } catch {
      return { status: null, message: null, confirmation: null, success: undefined }
    }
  }

  if (typeof parsed !== 'object' || parsed == null) {
    return { status: null, message: null, confirmation: null, success: undefined }
  }

  const record = parsed as Record<string, unknown>
  const status = record.status
  const message = record.message
  const confirmation = record.confirmation
  const success = record.success

  return {
    status:
      status === 'pending' || status === 'approved' || status === 'rejected' || status === 'failed' ? status : null,
    message: typeof message === 'string' ? message : null,
    confirmation: isCopilotActionConfirmation(confirmation) ? confirmation : null,
    success: typeof success === 'boolean' ? success : undefined
  }
}

/** Narrows an unknown payload to the confirmation copy the card needs. */
const isCopilotActionConfirmation = (value: unknown): value is CopilotActionConfirmation => {
  if (typeof value !== 'object' || value == null) return false

  const candidate = value as Partial<CopilotActionConfirmation>

  return (
    typeof candidate.title === 'string' &&
    typeof candidate.description === 'string' &&
    typeof candidate.confirmLabel === 'string' &&
    typeof candidate.appliedLabel === 'string' &&
    typeof candidate.cancelledLabel === 'string' &&
    Array.isArray(candidate.items)
  )
}

/** Builds the result sent back to the agent once the user has decided. */
export const buildCopilotConfirmedActionDecisionResult = ({
  decision,
  confirmation,
  outcome
}: {
  decision: CopilotConfirmedActionDecision
  confirmation: CopilotActionConfirmation
  /** The executor's result. Omitted when the user cancelled. */
  outcome?: ToolResult
}): CopilotConfirmedActionToolResult => {
  if (decision === 'rejected') {
    // success: true — the HITL turn completed; the user declined. success: false
    // looks like a tool failure and often makes the agent re-call the same tool
    // (especially after a single/multi pick), which reopens the confirmation card.
    // Forbid highlight_element / other tools: agents otherwise pulse page UI when
    // acknowledging cancel (highlight_element is framed as “when you mention UI”).
    return {
      success: true,
      status: 'rejected',
      message: `The user cancelled: ${confirmation.title} Nothing was changed. Reply with a brief acknowledgement only, then stop. Do not call any tools (including highlight_element, inspect_current_page, or this tool again) and do not highlight or point at page UI unless the user explicitly asks again.`,
      confirmation
    }
  }

  return {
    ...outcome,
    success: outcome?.success ?? false,
    status: outcome?.success === false ? 'failed' : 'approved',
    message: outcome?.message ?? 'The action was confirmed, but its result is unknown.',
    confirmation
  }
}
